import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { Contact, ContactApi, PatientInfo } from 'apps/wrapper/src/services/api-client/contact-api';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentAvailability } from 'apps/wrapper/src/services/appointment/types/appointment-availability';
import { AppointmentRequestDetails } from 'apps/wrapper/src/services/appointment/types/appointment-request-details';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { PmsWritebackStatus } from 'apps/wrapper/src/services/appointment/types/pms-writeback-status';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useApptRequestsWidget } from '../use-appt-requests-widget';
import { RequestDetailsViewContext } from './context';

export interface RequestDetailsViewProviderProps {
  apptRequestId: number;
  children: ReactNode;
}

export const RequestDetailsViewProvider: FC<RequestDetailsViewProviderProps> = (props) => {
  const { apptRequestId } = props;
  const { config } = useConfig();
  const { showError } = useNotification();
  const { oidcService, logoutRedirect } = useOidcWithAdmin();
  const { locationId } = useApptRequestsWidget();
  const { goBack } = useApptRequestsWidget();
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState<Contact>();
  const [patientInfo, setPatientInfo] = useState<PatientInfo>();
  const [requestDetails, setRequestDetails] = useState<AppointmentRequestDetails>(null!);
  const [appointmentAvailabilities, setAppointmentAvailabilities] = useState<AppointmentAvailability[]>();
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);
  const contactApi = ContactApi.getInstance();

  const isEditable = useMemo(() => {
    if (!requestDetails) return false;
    return (
      (requestDetails.status === AppointmentRequestStatus.New ||
        requestDetails.status === AppointmentRequestStatus.Pending) &&
      requestDetails.pmsWritebackStatus != PmsWritebackStatus.Pending &&
      !requestDetails.isDirectlyBooked
    );
  }, [requestDetails]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const apptRequestDetails = await loadApptRequestDetails(apptRequestId, locationId);

      if (!apptRequestDetails) {
        goBack();

        return;
      }

      if (apptRequestDetails.patient.id) {
        const [pInfo, contactDetails] = await Promise.all([
          contactApi.getPatientInfoById(apptRequestDetails.patient.id),
          contactApi.getContactDetailsById(apptRequestDetails.patient.id)
        ]);

        setPatientInfo(pInfo);
        setContact(contactDetails);
      }

      // not awaiting this call to disaply the details page faster
      loadAppointmentAvailability(apptRequestId, locationId);
      setRequestDetails(apptRequestDetails);
      setIsLoading(false);
    };

    init();
  }, [apptRequestId, locationId]);

  const loadApptRequestDetails = async (apptReqId: number, locId: number) => {
    try {
      return await appointmentService.getAppointmentRequestDetails(apptReqId, locId);
    } catch (error: any) {
      if (error?.status === 401 || error?.status === 403) logoutRedirect();

      showError('Unable to load appointment request details');

      return null;
    }
  };

  const loadAppointmentAvailability = async (apptReqId: number, locId: number) => {
    try {
      const availabilities = await appointmentService.getAppointmentAvailability(locId, apptReqId);

      setAppointmentAvailabilities(availabilities);
    } catch (error: any) {
      if (error?.status === 401 || error?.status === 403) logoutRedirect();

      showError('Unable to load appointment request availability');
      setAppointmentAvailabilities([]);
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      contact,
      isEditable,
      patientInfo,
      requestDetails,
      appointmentAvailabilities
    }),
    [isLoading, contact, isEditable, patientInfo, requestDetails, appointmentAvailabilities]
  );

  return <RequestDetailsViewContext.Provider value={contextValue}>{props.children}</RequestDetailsViewContext.Provider>;
};
